import styled from "styled-components"
import Header from "../Common/Header"
import Footer from "../Common/Footer"

const Main = styled.main`
  background: #f5f8fb;
  color: ${({ theme }) => theme.textPrimary};
`

const Hero = styled.header`
  padding: clamp(4.5rem, 10vw, 7rem) 1.5rem clamp(3rem, 7vw, 5rem);
  background: linear-gradient(145deg, #0a1f35 0%, #0d4172 62%, #337abd 100%);
  color: #fff;
`

const HeroInner = styled.div`
  width: min(920px, 100%);
  margin: 0 auto;

  span {
    display: block;
    margin-bottom: 0.8rem;
    color: #a9dcff;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
  }

  h1 {
    color: #fff;
    font-size: clamp(2.4rem, 7vw, 4rem);
    max-width: 760px;
  }

  p {
    color: rgba(255, 255, 255, 0.84);
    margin: 1rem 0 0;
  }
`

const Content = styled.div`
  width: min(920px, calc(100% - 2rem));
  margin: 0 auto;
  padding: clamp(3rem, 7vw, 5.5rem) 0;
  display: grid;
  gap: 2.5rem;
`

const Lead = styled.p`
  padding: 1.25rem 1.4rem;
  border-left: 4px solid ${({ theme }) => theme.primary};
  border-radius: 0 2px 2px 0;
  background: #fff;
  box-shadow: 0 14px 36px rgba(13, 65, 114, 0.08);
`

const Section = styled.section`
  display: grid;
  gap: 0.8rem;

  h2 {
    color: ${({ theme }) => theme.primaryDark};
    font-size: clamp(1.45rem, 4vw, 2rem);
  }

  p,
  li {
    line-height: 1.75;
  }

  p {
    margin: 0;
  }

  ul {
    margin: 0;
    padding-left: 1.25rem;
    display: grid;
    gap: 0.45rem;
  }

  a {
    color: ${({ theme }) => theme.primaryDark};
    font-weight: 700;
  }
`

const PrivacyPolicy = () => (
  <>
    <Header />
    <Main id="contenu-principal">
      <Hero>
        <HeroInner>
          <span>Protection des renseignements personnels</span>
          <h1>Politique de confidentialité</h1>
          <p>Dernière mise à jour : 21 août 2026</p>
        </HeroInner>
      </Hero>

      <Content>
        <Lead>
          Cette politique explique quels renseignements TB Gestion Immobilière peut recueillir par son site, pourquoi ils sont utilisés et comment
          exercer vos droits. Elle s’applique aux formulaires, à Espace TB et aux témoins de navigation du site.
        </Lead>

        <Section>
          <h2>1. Renseignements recueillis</h2>
          <p>Selon le formulaire que vous utilisez, nous pouvons recueillir :</p>
          <ul>
            <li>vos nom, courriel et numéro de téléphone;</li>
            <li>les renseignements que vous fournissez sur un immeuble, un projet de gestion ou une demande de service;</li>
            <li>les critères et renseignements nécessaires au traitement d’une demande de location;</li>
            <li>les renseignements de compte Espace TB, les messages, statuts et photos ajoutés à une demande de service;</li>
            <li>des données techniques limitées, comme le type de navigateur, l’adresse IP et les interactions avec le site.</li>
          </ul>
          <p>Nous vous demandons de ne pas transmettre de renseignements qui ne sont pas nécessaires à votre demande.</p>
        </Section>

        <Section>
          <h2>2. Utilisation</h2>
          <p>
            Ces renseignements servent à répondre à vos demandes, évaluer un besoin de gestion, traiter une candidature locative, fournir les services
            convenus, fournir les suivis et avis d’Espace TB, assurer la sécurité du site et respecter nos obligations légales. La mesure d’audience
            et la publicité ne sont activées qu’avec votre consentement.
          </p>
        </Section>

        <Section>
          <h2>3. Communication à des fournisseurs</h2>
          <p>
            Nous ne vendons pas vos renseignements personnels. Des fournisseurs de services — par exemple l’hébergement, le courriel, les outils de
            formulaire, le stockage privé des pièces jointes ou la mesure d’audience — peuvent y avoir accès uniquement dans la mesure nécessaire à
            leur mandat. Certains fournisseurs peuvent traiter des données à l’extérieur du Québec; nous évaluons alors les mesures de protection
            applicables.
          </p>
        </Section>

        <Section>
          <h2>4. Conservation et sécurité</h2>
          <p>
            Nous conservons les renseignements seulement pendant la période nécessaire aux fins décrites ou exigée par la loi. Nous appliquons des
            mesures administratives, techniques et physiques raisonnables pour limiter l’accès, la perte et l’utilisation non autorisée. Les accès
            Espace TB sont individuels; les pièces jointes sont conservées dans un stockage privé et rendues accessibles temporairement aux personnes
            autorisées. Aucun système connecté à Internet ne peut toutefois offrir une sécurité absolue.
          </p>
        </Section>

        <Section>
          <h2>5. Témoins et mesure d’audience</h2>
          <p>
            Les témoins essentiels assurent le fonctionnement et la sécurité du site. Les témoins de mesure d’audience ou de publicité demeurent
            désactivés jusqu’à votre choix dans le panneau de préférences. Vous pouvez modifier ce choix en supprimant le témoin de consentement dans
            votre navigateur, puis en rechargeant le site.
          </p>
        </Section>

        <Section>
          <h2>6. Vos droits</h2>
          <p>
            Sous réserve des exceptions prévues par la loi, vous pouvez demander l’accès à vos renseignements, leur rectification ou leur suppression,
            ou retirer un consentement. Nous pouvons devoir vérifier votre identité avant de répondre.
          </p>
        </Section>

        <Section>
          <h2>7. Nous joindre</h2>
          <p>
            Écrivez au responsable de la protection des renseignements personnels de TB Gestion Immobilière à{" "}
            <a href="mailto:info@tbgestionimmobiliere.com">info@tbgestionimmobiliere.com</a> ou téléphonez au{" "}
            <a href="tel:14186849004">(418) 684-9004</a>. Nous pouvons mettre cette politique à jour; la date affichée en haut indique la version en
            vigueur.
          </p>
        </Section>
      </Content>
    </Main>
    <Footer />
  </>
)

export default PrivacyPolicy
